/**
 * <terms-section> — T&C layout with page-link tabs and an optional content search.
 *
 * - Tabs are plain page links (active = current page via aria-current, server-rendered);
 *   the horizontal mobile rail is the global <swiper-init>, so no tab JS is needed here.
 * - The optional search filters the on-page text groups (show matching, hide the rest),
 *   surfacing a no-results banner with the typed term. Hiding is scoped via `is-hidden`.
 */

class TermsSection extends HTMLElement {
  #initialized = false;
  #groups = [];
  #filterTimer = null;

  constructor() {
    super();
    this.onInput = this.onInput.bind(this);
    this.searchTerm = '';
  }

  connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;

    this.#cacheDom();
    this.searchInput?.addEventListener('input', this.onInput);
  }

  disconnectedCallback() {
    if (!this.#initialized) return;
    this.#initialized = false;

    clearTimeout(this.#filterTimer);
    this.#filterTimer = null;
    this.searchInput?.removeEventListener('input', this.onInput);

    this.searchInput = null;
    this.searchStatus = null;
    this.noResults = [];
    this.#groups = [];
    this.searchTerm = '';
  }

  onInput(event) {
    this.searchTerm = event.target.value.trim().toLowerCase();
    clearTimeout(this.#filterTimer);

    if (!this.searchTerm) {
      this.#filterTimer = null;
      this.filterBySearch();
      return;
    }

    // timeout: debounce filtering until the user pauses typing (200ms) so we don't re-scan
    // every text group on each keystroke. Cleared on new input and in disconnectedCallback.
    this.#filterTimer = setTimeout(() => this.filterBySearch(), 200);
  }

  filterBySearch() {
    if (!this.searchTerm) {
      this.#groups.forEach(({ el }) => el.classList.remove('is-hidden'));
      this.setNoResultsVisible(false);
      this.#announce('');
      return;
    }

    const results = this.#groups.map(({ el, text }) => ({
      el,
      matches: text.includes(this.searchTerm),
    }));
    const matchCount = results.filter((r) => r.matches).length;
    const hasMatch = matchCount > 0;

    if (hasMatch) {
      results.forEach(({ el, matches }) => el.classList.toggle('is-hidden', !matches));
    } else {
      // No match — keep every section browsable and just surface the banner (matches FAQ),
      // instead of collapsing the whole section to only the message.
      this.#groups.forEach(({ el }) => el.classList.remove('is-hidden'));
      this.renderNoResults();
    }

    this.setNoResultsVisible(!hasMatch);
    this.#announce(matchCount);
  }

  // Announce the filter outcome to screen readers via the persistent live region ('' clears it on
  // reset). textContent keeps the count / typed term XSS-safe, like #renderNoResults.
  #announce(count) {
    if (!this.searchStatus) return;
    if (count === '') {
      this.searchStatus.textContent = '';
      return;
    }
    if (count > 0) {
      const template = this.searchStatus.dataset.termsResultsTemplate ?? '';
      this.searchStatus.textContent = template.replaceAll('%%COUNT%%', String(count));
    } else {
      const term = this.searchInput?.value.trim() ?? '';
      const template = this.searchStatus.dataset.termsEmptyTemplate ?? '';
      this.searchStatus.textContent = template.replaceAll('%%TERM%%', term);
    }
  }

  setNoResultsVisible(isVisible) {
    this.classList.toggle('is-no-results', isVisible);
    this.noResults.forEach((el) => {
      el.classList.toggle('is-hidden', !isVisible);
    });
  }

  renderNoResults() {
    // Inject the typed term (original casing) into the localized message. textContent = XSS-safe.
    const term = this.searchInput?.value.trim() ?? '';
    this.noResults.forEach((el) => {
      const textEl = el.querySelector('[data-terms-no-results-text]');
      const template = el.dataset.termsNoResultsTemplate ?? '';
      if (textEl) textEl.textContent = template.replaceAll('%%TERM%%', term);
    });
  }

  #cacheDom() {
    this.searchInput = this.querySelector('[data-terms-search-input]');
    this.searchStatus = this.querySelector('[data-terms-search-status]');
    this.noResults = Array.from(this.querySelectorAll('[data-terms-no-results]'));
    this.#groups = Array.from(this.querySelectorAll('[data-terms-group]')).map((el) => ({
      el,
      text: (el.textContent ?? '').toLowerCase(),
    }));
  }
}

if (!customElements.get('terms-section')) {
  customElements.define('terms-section', TermsSection);
}
